/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-restricted-syntax */

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import useScrollToTopOfProductList from 'components/shared/productList/shared/hooks/useScrollToTopOfProductList';
import { resetPagination } from '../../../Pagination/shared/hooks/usePagination';
import deleteSearchParamByKeyValue from '../helpers';
import useUncommittedSearchParams from './useUncommittedSearchParams';

export default function useFilters() {
    const {
        uncommittedSearchParams,
        toggleUncommittedSearchParam,
        addUncommittedSearchParamByValue,
        removeUncommittedSearchParam,
        removeUncommittedSearchParamByValue,
        updateUncommittedSearchParam,
        commitUncommittedSearchParams,
        isUncommittedSearchParamActive,
        isUncommittedSearchParamValueActive,
        resetUncommittedSearchParams,
        hasUncommittedSearchParams
    } = useUncommittedSearchParams();

    const [searchParams, setSearchParams] = useSearchParams();
    const scrollToTopOfProductList = useScrollToTopOfProductList();

    /**
     * Check if filter is active
     * @param queryStringName
     * @returns
     */
    const isFilterActive = useCallback((queryStringName: string) => searchParams.has(queryStringName), [searchParams]);
    /**
     * Check if filter with a specific value is active
     * @param queryStringName
     * @returns
     */
    const isFilterValueActive = useCallback(
        (queryStringName: string, value: string) => searchParams.getAll(queryStringName).includes(value),
        [searchParams]
    );

    /**
     * Update a filter (Updates all instances of filter with that queryString)
     * @param queryStringName
     * @param value
     */
    const updateFilter = useCallback(
        (queryStringName: string, value: string) => {
            setSearchParams((previousSearchParams) => {
                resetPagination(previousSearchParams);
                previousSearchParams.delete(queryStringName);
                previousSearchParams.append(queryStringName, value);

                return previousSearchParams;
            });
        },
        [setSearchParams]
    );

    /**
     * Update a filter by its queryString and value (only updates that specific queryString with value)
     * @param queryStringName
     * @param value
     */
    const updateFilterByValue = useCallback(
        (queryStringName: string, value: string) => {
            setSearchParams((previousSearchParams) => {
                resetPagination(previousSearchParams);
                deleteSearchParamByKeyValue(previousSearchParams, queryStringName, value);
                previousSearchParams.append(queryStringName, value);

                return previousSearchParams;
            });
        },
        [setSearchParams]
    );

    /**
     * Remove a filter (Removes all instances of filter with that queryString)
     * @param queryStringName
     */
    const removeFilter = useCallback(
        (queryStringName: string) => {
            setSearchParams((previousSearchParams) => {
                resetPagination(previousSearchParams);
                previousSearchParams.delete(queryStringName);

                return previousSearchParams;
            });
        },
        [setSearchParams]
    );

    /**
     * Remove a filter by its queryString and value (only removes that specific queryString with value)
     * @param queryStringName
     * @param value
     */
    const removeFilterByValue = useCallback(
        (queryStringName: string, value: string) => {
            setSearchParams((previousSearchParams) => {
                resetPagination(previousSearchParams);
                deleteSearchParamByKeyValue(previousSearchParams, queryStringName, value);

                return previousSearchParams;
            });
        },
        [setSearchParams]
    );

    /**
     * Remove filters by their queryStrings
     * @param queryStringName
     * @param value
     */
    const removeFiltersByQueryStrings = useCallback(
        (queryStringNames: string[]) => {
            setSearchParams((previousSearchParams) => {
                resetPagination(previousSearchParams);

                queryStringNames.forEach((queryStringName) => {
                    previousSearchParams.delete(queryStringName);
                });

                return previousSearchParams;
            });
        },
        [setSearchParams]
    );

    /**
     * Add a filter
     * @param queryStringName
     * @param value
     * @returns
     */
    const addFilter = useCallback(
        (queryStringName: string, value: string) => {
            const isFilterApplied = isFilterValueActive(queryStringName, value);
            if (isFilterApplied) return;

            setSearchParams((previousSearchParams) => {
                resetPagination(previousSearchParams);
                previousSearchParams.append(queryStringName, value);

                return previousSearchParams;
            });
        },
        [isFilterValueActive, setSearchParams]
    );

    /**
     * Toggle a filter value on/off by its queryStringName and value
     * @param queryStringName
     * @param value
     */
    const toggleFilterByValue = useCallback(
        (queryStringName: string, value: string) => {
            const isFilterApplied = isFilterValueActive(queryStringName, value);

            if (!isFilterApplied) {
                addFilter(queryStringName, value);
            } else {
                removeFilterByValue(queryStringName, value);
            }
        },
        [addFilter, isFilterValueActive, removeFilterByValue]
    );

    /**
     * Removes all filters
     * @returns
     */
    const removeAllFilters = useCallback(() => {
        setSearchParams(new URLSearchParams());
        scrollToTopOfProductList();
    }, [scrollToTopOfProductList, setSearchParams]);

    /**
     * Applies all pending filters and scrolls window to top of ProductList
     */
    const handleApplyPendingFilters = useCallback(() => {
        const hasPendingFilters = hasUncommittedSearchParams;

        if (hasPendingFilters) {
            commitUncommittedSearchParams();
            scrollToTopOfProductList();
        }
    }, [commitUncommittedSearchParams, hasUncommittedSearchParams, scrollToTopOfProductList]);

    return {
        activeFilters: searchParams,
        addFilter,
        updateFilter,
        updateFilterByValue,
        removeFilter,
        removeFilterByValue,
        removeAllFilters,
        removeFiltersByQueryStrings,
        isFilterActive,
        isFilterValueActive,
        toggleFilterByValue,

        // Methods for Pending filters
        pendingFilters: uncommittedSearchParams,
        addPendingFilterByValue: addUncommittedSearchParamByValue,
        removePendingFilter: removeUncommittedSearchParam,
        removePendingFilterByValue: removeUncommittedSearchParamByValue,
        removeAllPendingFilters: resetUncommittedSearchParams,
        updatePendingFilter: updateUncommittedSearchParam,
        togglePendingFilterByValue: toggleUncommittedSearchParam,
        applyPendingFilters: handleApplyPendingFilters,
        isPendingFilterActive: isUncommittedSearchParamActive,
        isPendingFilterValueActive: isUncommittedSearchParamValueActive,
        hasPendingFilters: hasUncommittedSearchParams
    };
}
